/**
 * Product Page Enhancements
 * Handles interactive features for product page blocks
 */

class ProductPageEnhancements {
  constructor() {
    this.init();
  }

  init() {
    this.initReviewCarousels();
    this.initFeatureAnimations();
    this.initGuaranteeInteractions();
    this.initScrollAnimations();
  }

  /**
   * Initialize review carousels
   */
  initReviewCarousels() {
    const carousels = document.querySelectorAll('.customer-reviews-carousel');
    
    carousels.forEach(carousel => {
      this.setupReviewCarousel(carousel);
    });
  }

  setupReviewCarousel(carousel) {
    const slider = carousel.querySelector('.reviews-slider');
    const prevBtn = carousel.querySelector('.review-prev');
    const nextBtn = carousel.querySelector('.review-next');
    
    if (!slider || !prevBtn || !nextBtn) return;
    
    let currentIndex = 0;
    const slides = slider.querySelectorAll('.review-slide');
    const totalSlides = slides.length;
    
    if (totalSlides <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
      return;
    }
    
    // Set initial state
    slider.style.display = 'flex';
    
    const updateSlider = () => {
      const offset = -currentIndex * 100;
      slider.style.transform = `translateX(${offset}%)`;
      slider.style.transition = 'transform 0.5s ease-in-out';
    };
    
    const goToSlide = (index) => {
      currentIndex = index;
      if (currentIndex < 0) currentIndex = totalSlides - 1;
      if (currentIndex >= totalSlides) currentIndex = 0;
      updateSlider();
    };
    
    // Navigation events
    prevBtn.addEventListener('click', () => goToSlide(currentIndex - 1));
    nextBtn.addEventListener('click', () => goToSlide(currentIndex + 1));
    
    // Auto-play functionality
    let autoPlayInterval = setInterval(() => goToSlide(currentIndex + 1), 5000);
    
    // Pause auto-play on hover
    slider.addEventListener('mouseenter', () => clearInterval(autoPlayInterval));
    slider.addEventListener('mouseleave', () => {
      autoPlayInterval = setInterval(() => goToSlide(currentIndex + 1), 5000);
    });
    
    // Touch/swipe support
    let touchStartX = 0;
    let touchEndX = 0;
    
    slider.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    });
    
    slider.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX, goToSlide, currentIndex);
    });
    
    // Initialize
    updateSlider();
  }

  handleSwipe(startX, endX, goToSlide, currentIndex) {
    const swipeThreshold = 50;
    const diff = startX - endX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        goToSlide(currentIndex + 1); // Swipe left, go to next
      } else {
        goToSlide(currentIndex - 1); // Swipe right, go to previous
      }
    }
  }

  /**
   * Initialize feature animations
   */
  initFeatureAnimations() {
    const features = document.querySelectorAll('.feature-item');
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }, index * 100);
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    features.forEach(feature => {
      feature.style.opacity = '0';
      feature.style.transform = 'translateY(20px)';
      feature.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      observer.observe(feature);
    });
  }

  /**
   * Initialize guarantee interactions
   */
  initGuaranteeInteractions() {
    const guarantees = document.querySelectorAll('.guarantee-item');
    
    guarantees.forEach(guarantee => {
      guarantee.addEventListener('mouseenter', () => {
        guarantee.style.transform = 'translateY(-4px) scale(1.02)';
      });
      
      guarantee.addEventListener('mouseleave', () => {
        guarantee.style.transform = 'translateY(0) scale(1)';
      });
      
      // Add click interaction for mobile
      guarantee.addEventListener('click', () => {
        if (window.innerWidth <= 749) {
          guarantee.style.transform = 'scale(0.98)';
          setTimeout(() => {
            guarantee.style.transform = 'scale(1)';
          }, 150);
        }
      });
    });
  }

  /**
   * Initialize scroll animations
   */
  initScrollAnimations() {
    const animatedElements = document.querySelectorAll('.product-features, .product-guarantees, .customer-reviews-carousel');
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    animatedElements.forEach(element => {
      observer.observe(element);
    });
  }

  /**
   * Handle responsive behavior
   */
  handleResponsive() {
    const handleResize = () => {
      const features = document.querySelectorAll('.feature-item');
      const guarantees = document.querySelectorAll('.guarantee-item');
      
      // Adjust feature items for mobile
      if (window.innerWidth <= 749) {
        features.forEach(feature => {
          feature.style.flexDirection = 'column';
          feature.style.textAlign = 'center';
        });
      } else {
        features.forEach(feature => {
          feature.style.flexDirection = 'row';
          feature.style.textAlign = 'left';
        });
      }
    };
    
    window.addEventListener('resize', this.debounce(handleResize, 250));
    handleResize(); // Initial call
  }

  /**
   * Debounce function for performance
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Add smooth scroll behavior for anchor links
   */
  initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  /**
   * Initialize lazy loading for images
   */
  initLazyLoading() {
    const images = document.querySelectorAll('img[data-src]');
    
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.classList.remove('lazy');
          imageObserver.unobserve(img);
        }
      });
    });
    
    images.forEach(img => imageObserver.observe(img));
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ProductPageEnhancements();
});

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductPageEnhancements;
}
